// src/components/Layout.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../redux/store";
import { logout } from "../redux/slices/authSlice";
import { toggleTheme } from "../redux/slices/themeSlice";
import {
  getUnreadChatCount,
  addMessage,
  setCurrentRecovery,
} from "../redux/slices/recoveryChatSlice";
import { GlobalStyles } from "../styles/globalStyles";
import { pusherService } from "../services/api";
import { isAdmin } from "../types";
import logo from "../assets/images/logo.png";
import {
  Home,
  Wallet as WalletIcon,
  CreditCard,
  Shield,
  User,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  Users,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Lock,
  Briefcase,
  ChevronDown,
  Info,
  MessageSquare,
  Bell,
  Wifi,
  WifiOff,
  Loader2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const { mode } = useSelector((state: RootState) => state.theme);
  const { unreadCount } = useSelector(
    (state: RootState) => state.recoveryChats,
  );

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    "disconnected" | "connecting" | "connected" | "error"
  >("disconnected");
  const [pusherInitialized, setPusherInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  const initializationRef = useRef<boolean>(false);
  const tokenRef = useRef<string | null>(null);
  const mountedRef = useRef<boolean>(true);
  const subscribeToRecoveryChannelsRef = useRef<(() => void) | null>(null);
  const currentChatRef = useRef<{ caseId: number; isAdmin: boolean } | null>(
    null,
  );
  const lastAuthCheckRef = useRef<number>(0);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isUserAdmin = user ? isAdmin(user) : false;

  const navigation = [
    { name: "Dashboard", path: "/dashboard", icon: Home, key: "dashboard" },
    { name: "Wallet", path: "/wallet", icon: WalletIcon, key: "wallet" },
    { name: "Deposit", path: "/deposit", icon: ArrowDownRight, key: "deposit" },
    {
      name: "Withdraw",
      path: "/withdraw",
      icon: ArrowUpRight,
      key: "withdraw",
    },
    {
      name: "Transactions",
      path: "/transactions",
      icon: CreditCard,
      key: "transactions",
    },
    { name: "Recovery", path: "/recovery", icon: Shield, key: "recovery" },
    { name: "Profile", path: "/profile", icon: User, key: "profile" },
    ...(isUserAdmin
      ? [
          {
            name: "Admin Dashboard",
            path: "/admin",
            icon: Briefcase,
            key: "admin-dashboard",
          },
          {
            name: "Users",
            path: "/admin/users",
            icon: Users,
            key: "admin-users",
          },
          {
            name: "Admin Transactions",
            path: "/admin/transactions",
            icon: CreditCard,
            key: "admin-transactions",
          },
          { name: "KYC", path: "/admin/kyc", icon: Lock, key: "admin-kyc" },
          {
            name: "Recovery Cases",
            path: "/admin/recovery",
            icon: FileText,
            key: "admin-recovery",
          },
        ]
      : []),
  ];

  useEffect(() => {
    const handleChatOpen = (event: CustomEvent) => {
      const { caseId, isAdmin } = event.detail;
      currentChatRef.current = { caseId, isAdmin };
      dispatch(setCurrentRecovery(caseId));
    };

    const handleChatClose = () => {
      currentChatRef.current = null;
      dispatch(setCurrentRecovery(null));
    };

    window.addEventListener("chat-open" as any, handleChatOpen);
    window.addEventListener("chat-close" as any, handleChatClose);

    return () => {
      window.removeEventListener("chat-open" as any, handleChatOpen);
      window.removeEventListener("chat-close" as any, handleChatClose);
    };
  }, [dispatch]);

  useEffect(() => {
    subscribeToRecoveryChannelsRef.current = () => {
      if (!user || !pusherService.getInstance() || !mountedRef.current) return;

      console.log("📡 Subscribing to SINGLE recovery channel for user:", {
        userId: user.id,
        userRole: user.role,
        isAdmin: isUserAdmin,
      });

      try {
        if (isUserAdmin) {
          console.log("👔 Admin detected - subscribing to admin.recovery ONLY");
          const adminChannel = pusherService.subscribeToChannel(
            "private-admin.recovery",
          );

          if (adminChannel) {
            adminChannel.bind("pusher:subscription_succeeded", () => {
              console.log(
                "✅ Subscribed to admin.recovery (ONLY channel for admin)",
              );
            });

            adminChannel.bind("RecoveryMessageSent", (data: any) => {
              if (!data?.chat) return;

              const chat = data.chat;
              console.log("📨 Admin channel message (admin viewing):", {
                messageId: chat.id,
                caseId: chat.recovery_request_id,
                isAdminMessage: chat.is_admin,
                fromUser: chat.user_id,
                fromAdmin: chat.admin_id,
              });

              dispatch(getUnreadChatCount() as any);

              const currentChat = currentChatRef.current;
              const isForCurrentChat =
                currentChat?.caseId === chat.recovery_request_id;

              if (isForCurrentChat) {
                console.log("📢 Dispatching pusher-message-received event");
                window.dispatchEvent(
                  new CustomEvent("pusher-message-received", {
                    detail: {
                      message: chat,
                      caseId: chat.recovery_request_id,
                    },
                  }),
                );
              }

              console.log(
                "➕ Adding message to Redux store from admin channel",
              );
              dispatch(addMessage(chat));
            });
          }
        } else {
          console.log(
            "👤 Regular user detected - subscribing to user channel ONLY",
          );
          const userChannel = pusherService.subscribeToChannel(
            `private-user.recovery.${user.id}`,
          );

          if (userChannel) {
            userChannel.bind("pusher:subscription_succeeded", () => {
              console.log(
                `✅ Subscribed to user.recovery.${user.id} (ONLY channel for user)`,
              );
            });

            userChannel.bind("RecoveryMessageSent", (data: any) => {
              if (!data?.chat) return;

              const chat = data.chat;
              console.log("📨 User channel message (user viewing):", {
                messageId: chat.id,
                caseId: chat.recovery_request_id,
                isAdminMessage: chat.is_admin,
                fromUser: chat.user_id,
                fromAdmin: chat.admin_id,
              });

              dispatch(getUnreadChatCount() as any);

              const currentChat = currentChatRef.current;
              const isForCurrentChat =
                currentChat?.caseId === chat.recovery_request_id;

              if (isForCurrentChat) {
                console.log("📢 Dispatching pusher-message-received event");
                window.dispatchEvent(
                  new CustomEvent("pusher-message-received", {
                    detail: {
                      message: chat,
                      caseId: chat.recovery_request_id,
                    },
                  }),
                );
              }

              console.log("➕ Adding message to Redux store from user channel");
              dispatch(addMessage(chat));
            });
          }
        }
      } catch (error) {
        console.error("❌ Error subscribing to channel:", error);
      }
    };
  }, [user, isUserAdmin, dispatch]);

  const initializePusher = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!isAuthenticated || !user || !token) {
      return;
    }

    if (
      initializationRef.current &&
      tokenRef.current === token &&
      pusherService.getInstance()
    ) {
      console.log(
        "✅ Pusher already initialized with current token, skipping...",
      );
      return;
    }

    if (initializationTimeoutRef.current) {
      clearTimeout(initializationTimeoutRef.current);
      initializationTimeoutRef.current = null;
    }

    const now = Date.now();
    if (now - lastAuthCheckRef.current < 1000) {
      console.log("⏸️ Rate limiting Pusher initialization - too soon");
      return;
    }

    lastAuthCheckRef.current = now;

    console.log("🚀 Initializing Pusher for user:", {
      id: user.id,
      email: user.email,
      role: user.role,
      timestamp: new Date().toISOString(),
    });

    initializationRef.current = true;
    tokenRef.current = token;
    setConnectionStatus("connecting");

    try {
      const existingInstance = pusherService.getInstance();
      if (existingInstance && tokenRef.current !== token) {
        console.log("🔄 Token changed, reinitializing Pusher");
        pusherService.disconnect();
      } else if (existingInstance) {
        console.log("♻️ Reusing existing Pusher instance");
      }

      const pusher = pusherService.initialize(token);

      pusher.connection.bind("connected", () => {
        console.log("✅ Pusher connected successfully");
        setConnectionStatus("connected");
        setRetryCount(0);
        setPusherInitialized(true);

        setTimeout(() => {
          if (subscribeToRecoveryChannelsRef.current && mountedRef.current) {
            subscribeToRecoveryChannelsRef.current();
          }
        }, 500);
      });

      pusher.connection.bind("error", (error: any) => {
        console.error("❌ Pusher connection error:", error);
        setConnectionStatus("error");
        setPusherInitialized(false);
        initializationRef.current = false;

        setTimeout(() => {
          if (mountedRef.current && isAuthenticated) {
            console.log("🔄 Auto-retrying Pusher connection after error");
            initializationRef.current = false;
            initializePusher();
          }
        }, 3000);
      });

      pusher.connection.bind("disconnected", () => {
        console.log("🔌 Pusher disconnected");
        setConnectionStatus("disconnected");
        setPusherInitialized(false);
        initializationRef.current = false;
      });

      pusher.connection.bind("connecting", () => {
        console.log("🔄 Pusher connecting...");
        setConnectionStatus("connecting");
      });
    } catch (error) {
      console.error("❌ Failed to initialize Pusher:", error);
      setConnectionStatus("error");
      initializationRef.current = false;
      setPusherInitialized(false);
    }
  }, [isAuthenticated, user]);

  const cleanupPusher = useCallback(() => {
    console.log("🧹 Cleaning up Pusher...");

    if (initializationTimeoutRef.current) {
      clearTimeout(initializationTimeoutRef.current);
      initializationTimeoutRef.current = null;
    }

    pusherService.disconnect();
    setConnectionStatus("disconnected");
    setPusherInitialized(false);
    initializationRef.current = false;
    tokenRef.current = null;
  }, []);

  const handleLogout = () => {
    cleanupPusher();
    dispatch(logout() as any);
    navigate("/login");
  };

  useEffect(() => {
    if (!user) return;

    const fetchUnreadCount = () => {
      dispatch(getUnreadChatCount() as any);
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [dispatch, user]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", mode === "dark");
  }, [mode]);

  useEffect(() => {
    if (isAuthenticated && user && !pusherInitialized && mountedRef.current) {
      console.log(
        "🔄 Initial auth setup detected, queuing Pusher initialization...",
      );

      initializationTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          initializePusher();
        }
      }, 500);
    } else if (!isAuthenticated && pusherInitialized) {
      console.log("👋 User logged out, cleaning up Pusher...");
      cleanupPusher();
    }

    return () => {
      mountedRef.current = false;
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [isAuthenticated, user, initializePusher, cleanupPusher]);

  const handleRetryConnection = () => {
    console.log("🔄 Manual retry initiated");
    setRetryCount((prev) => prev + 1);
    if (retryCount >= maxRetries) {
      toast.error("Max retries reached. Please refresh the page.");
      return;
    }
    initializationRef.current = false;
    setPusherInitialized(false);
    initializePusher();
  };

  if (
    !isAuthenticated &&
    ![
      "/login",
      "/register",
      "/verify-email",
      "/forgot-password",
      "/reset-password",
    ].includes(location.pathname)
  ) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 transition-colors">
      {/* Desktop Sidebar */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r border-gray-200/30 dark:border-gray-700/30 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl hidden lg:block shadow-lg">
        {/* Background Glow */}
        <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-primary/2 to-transparent rounded-r-3xl blur-xl opacity-50"></div>

        {/* Logo Section */}
        <div className="flex h-16 items-center justify-center border-b border-gray-200/30 dark:border-gray-700/30">
          <Link to="/dashboard" className="flex items-center gap-2 group/logo  ">
            <div className="relative">
              <img
                src={logo}
                alt="ObeliskRecovery"
                className="h-14 w-auto transition-transform group-hover/logo:scale-105"
              />
              <div className="absolute top-1 right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <span className={`${GlobalStyles.heading3} text-primary`}>Syvron Recovery</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive =
                location.pathname === item.path ||
                (item.path !== "/dashboard" &&
                  location.pathname.startsWith(item.path));

              const showUnreadCount =
                (isUserAdmin &&
                  item.name === "Recovery Cases" &&
                  unreadCount > 0) ||
                (!isUserAdmin && item.name === "Recovery" && unreadCount > 0);

              return (
                <Link
                  key={item.key}
                  to={item.path}
                  className={`group/nav flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-300 ${
                    isActive
                      ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary border-l-4 border-primary shadow-sm"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 hover:border-l-2 hover:border-primary/30"
                  }`}
                >
                  <Icon
                    className={`h-4 w-4 transition-transform group-hover/nav:scale-110 ${
                      isActive
                        ? "text-primary"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  />
                  <span className="flex-1">{item.name}</span>
                  {showUnreadCount && (
                    <span
                      className={`${GlobalStyles.badgeNotification} static h-4 w-4 text-[10px] ml-auto animate-pulse`}
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer Section */}
        <div className="relative border-t border-gray-200/30 dark:border-gray-700/30 p-4">
          {/* Connection Status */}
          <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 border border-gray-200/30 dark:border-gray-700/30 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {connectionStatus === "connecting" ? (
                  <div className="h-2 w-2 rounded-full bg-primary animate-ping"></div>
                ) : connectionStatus === "connected" ? (
                  <Wifi className="h-3 w-3 text-green-500" />
                ) : connectionStatus === "error" ? (
                  <WifiOff className="h-3 w-3 text-red-500" />
                ) : (
                  <WifiOff className="h-3 w-3 text-gray-400" />
                )}
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {connectionStatus === "connected"
                    ? "Live"
                    : connectionStatus === "connecting"
                      ? "Connecting..."
                      : connectionStatus === "error"
                        ? "Connection Error"
                        : "Disconnected"}
                </span>
              </div>
              {unreadCount > 0 && (
                <div className="flex items-center gap-1">
                  <Bell className="h-3 w-3 text-red-500 animate-bounce" />
                  <span className="text-xs font-bold text-red-500">
                    {unreadCount}
                  </span>
                </div>
              )}
            </div>

            {connectionStatus === "error" && retryCount < maxRetries && (
              <button
                onClick={handleRetryConnection}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} w-full group/retry`}
              >
                <span>Retry Connection</span>
                <Sparkles className="h-3 w-3 ml-1 opacity-0 group-hover/retry:opacity-100 transition-opacity" />
              </button>
            )}
          </div>

          {/* Logout Button */}
          <div className="w-full">
            <button
              onClick={handleLogout}
              className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full group/logout`}
            >
              <LogOut className="h-4 w-4 mr-2 transition-transform group-hover/logout:-translate-x-0.5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="fixed left-0 top-0 z-50 h-screen w-64 border-r border-gray-200/30 dark:border-gray-700/30 bg-white dark:bg-gray-900 backdrop-blur-xl lg:hidden animate-slide-in shadow-2xl">
            <div className="flex h-16 items-center justify-between border-b border-gray-200/30 dark:border-gray-700/30 px-4">
              <Link to="/dashboard" className="flex items-center">
                <img src={logo} alt="ObeliskRecovery" className="h-12 w-auto" />
                <div className="ml-2 h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className={`${GlobalStyles.iconButton} text-gray-700 dark:text-gray-300 hover:bg-primary/10`}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;

                  const showUnreadCount =
                    (isUserAdmin &&
                      item.name === "Recovery Cases" &&
                      unreadCount > 0) ||
                    (!isUserAdmin &&
                      item.name === "Recovery" &&
                      unreadCount > 0);

                  return (
                    <Link
                      key={item.key}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`group/nav flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-300 ${
                        isActive
                          ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary border-l-4 border-primary"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-700/50 hover:border-l-2 hover:border-primary/30"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          isActive
                            ? "text-primary"
                            : "text-gray-500 dark:text-gray-400"
                        }`}
                      />
                      <span className="flex-1">{item.name}</span>
                      {showUnreadCount && (
                        <span
                          className={`${GlobalStyles.badgeNotification} static h-4 w-4 text-[10px] ml-auto animate-pulse`}
                        >
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </nav>

            <div className="px-4 pb-4">
              <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-900/30 border border-gray-200/30 dark:border-gray-700/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {connectionStatus === "connecting" ? (
                      <div className="h-2 w-2 rounded-full bg-primary animate-ping"></div>
                    ) : connectionStatus === "connected" ? (
                      <Wifi className="h-3 w-3 text-green-500" />
                    ) : connectionStatus === "error" ? (
                      <WifiOff className="h-3 w-3 text-red-500" />
                    ) : (
                      <WifiOff className="h-3 w-3 text-gray-400" />
                    )}
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {connectionStatus}
                    </span>
                  </div>
                  {unreadCount > 0 && (
                    <div className="flex items-center gap-1">
                      <Bell className="h-3 w-3 text-red-500" />
                      <span className="text-xs font-bold text-red-500">
                        {unreadCount}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={() => {
                  handleLogout();
                  setSidebarOpen(false);
                }}
                className={`${GlobalStyles.button} w-full ${GlobalStyles.buttonMedium} ${GlobalStyles.buttonOutline} group/logout`}
              >
                <LogOut className="h-4 w-4 mr-2 transition-transform group-hover/logout:-translate-x-0.5" />
                <span>Logout</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-gray-200/30 dark:border-gray-700/30 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl px-4 lg:px-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className={`${GlobalStyles.iconButton} text-gray-700 dark:text-gray-300 lg:hidden hover:bg-primary/10`}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                {location.pathname.split("/")[1]?.charAt(0).toUpperCase() +
                  location.pathname.split("/")[1]?.slice(1) || "Dashboard"}
              </h1>
              <Sparkles className="h-3 w-3 text-primary/60" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Connection Status Indicator */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/30 dark:to-gray-900/30 border border-gray-200/30 dark:border-gray-700/30">
              <div
                className={`h-1.5 w-1.5 rounded-full ${
                  connectionStatus === "connected"
                    ? "bg-green-500 animate-pulse"
                    : connectionStatus === "connecting"
                      ? "bg-primary animate-ping"
                      : "bg-gray-400"
                }`}
              />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {connectionStatus === "connected"
                  ? "Live"
                  : connectionStatus === "connecting"
                    ? "Connecting"
                    : "Offline"}
              </span>
            </div>

            {/* Chat Notifications */}
            {unreadCount > 0 && (
              <Link
                to={isUserAdmin ? "/admin/recovery" : "/recovery"}
                className="relative p-2 rounded-lg hover:bg-primary/10 transition-all duration-300 group/chat"
                title={`${unreadCount} unread recovery message${
                  unreadCount !== 1 ? "s" : ""
                }`}
              >
                <MessageSquare className="h-5 w-5 text-primary transition-transform group-hover/chat:scale-110" />
                <span
                  className={`${GlobalStyles.badgeNotification} border border-white dark:border-gray-900 animate-pulse`}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </Link>
            )}

            {isUserAdmin && (
              <Link
                to="/admin"
                className={`${GlobalStyles.iconButton} text-gray-700 dark:text-gray-300 hidden sm:flex hover:bg-primary/10`}
                title="Admin Panel"
              >
                <Briefcase className="h-5 w-5 transition-transform hover:scale-110" />
              </Link>
            )}

            {/* Mobile user menu */}
            <div className="lg:hidden relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} flex items-center gap-2 group/user`}
              >
                <User className="h-4 w-4" />
                <ChevronDown className="h-3 w-3 transition-transform group-hover/user:rotate-180" />
              </button>

              {mobileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30 py-2 z-50">
                  {isUserAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary/10 transition-colors group/admin"
                    >
                      <Briefcase className="h-4 w-4 transition-transform group-hover/admin:scale-110" />
                      Admin Panel
                    </Link>
                  )}
                  {unreadCount > 0 && (
                    <Link
                      to={isUserAdmin ? "/admin/recovery" : "/recovery"}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-primary/10 transition-colors group/messages relative"
                    >
                      <MessageSquare className="h-4 w-4 text-primary transition-transform group-hover/messages:scale-110" />
                      <span>{isUserAdmin ? "Recovery Cases" : "Recovery"}</span>
                      <span
                        className={`${GlobalStyles.badgeNotification} static h-4 w-4 text-[10px] ml-auto`}
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    </Link>
                  )}
                  <div className="px-4 py-2 border-t border-gray-200/30 dark:border-gray-700/30">
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-1.5 w-1.5 rounded-full ${
                          connectionStatus === "connected"
                            ? "bg-green-500"
                            : connectionStatus === "connecting"
                              ? "bg-primary"
                              : "bg-gray-400"
                        }`}
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {connectionStatus}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left transition-colors group/logout-btn"
                  >
                    <LogOut className="h-4 w-4 transition-transform group-hover/logout-btn:-translate-x-0.5" />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={() => dispatch(toggleTheme())}
              className={`${GlobalStyles.iconButton} text-gray-700 dark:text-gray-300 hover:bg-primary/10`}
            >
              {mode === "dark" ? (
                <Sun className="h-5 w-5 transition-transform hover:rotate-12" />
              ) : (
                <Moon className="h-5 w-5 transition-transform hover:rotate-12" />
              )}
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="p-4 lg:p-5">
          {/* Admin Message */}
          {user?.display_message && (
            <div
              className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mb-6 backdrop-blur-sm`}
            >
              <Info className="h-5 w-5 flex-shrink-0 text-primary" />
              <div>
                <p className="text-sm">{user.display_message}</p>
              </div>
            </div>
          )}

          {/* Connection Status Banner */}
          {connectionStatus === "error" && (
            <div className="mb-6">
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning} backdrop-blur-sm`}
              >
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    Real-time connection failed
                  </p>
                  <p className="text-sm mt-1">
                    {retryCount < maxRetries
                      ? `Retrying... (${retryCount + 1}/${maxRetries})`
                      : "Max retries reached. Please refresh the page."}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  {retryCount < maxRetries && (
                    <button
                      onClick={handleRetryConnection}
                      className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} group/retry`}
                    >
                      <span>Retry Now</span>
                      <Sparkles className="h-3 w-3 ml-1 opacity-0 group-hover/retry:opacity-100 transition-opacity" />
                    </button>
                  )}
                  <button
                    onClick={() => window.location.reload()}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonSmall}`}
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          )}

          {connectionStatus === "connecting" && (
            <div className="mb-6">
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} backdrop-blur-sm`}
              >
                <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin" />
                <div>
                  <p className="text-sm font-medium">
                    Connecting to real-time service...
                  </p>
                  <p className="text-sm mt-1">
                    Establishing secure connection for live updates.
                  </p>
                </div>
              </div>
            </div>
          )}

          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
